/* eslint-disable no-unused-vars */
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import {
  FiUser,
  FiMail,
  FiMessageSquare,
  FiCheck,
  FiStar,
  FiPhone
} from "react-icons/fi";
import {
  FaTwitter,
  FaLinkedin,
  FaInstagram,
  FaRocket,
  FaFacebook
} from "react-icons/fa";
import Lottie from "lottie-react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";

// Import your Lottie JSON files
import loadingAnimation from "../assets/animations/Loading.json";
import successAnimation from "../assets/animations/success.json";
import errorAnimation from "../assets/animations/Error animation.json";
import contactAnimation from "../assets/animations/Contact us.json";
import sendAnimation from "../assets/animations/Message Sent Successfully _ Plane.json";

// API Configuration
const API_URL = `${process.env.REACT_APP_API_BASE_URL}/contact-messages`;
const SETTINGS_URL = `${process.env.REACT_APP_API_BASE_URL}/settings/ui`;

// Floating particles background component
const FloatingParticles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 5,
    duration: 3 + Math.random() * 4,
  }));

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute w-1 h-1 bg-blue-400 rounded-full opacity-20"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
          }}
          animate={{
            y: [0, -100, 0],
            x: [0, 20, -20, 0],
            opacity: [0, 0.3, 0],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Animated card component
const AnimatedCard = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, delay }}
    whileHover={{
      y: -5,
      scale: 1.02,
      transition: { duration: 0.2 }
    }}
    className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300"
  >
    {children}
  </motion.div>
);

// Social media icons mapping
const socialIcons = {
  twitter: FaTwitter,
  linkedin: FaLinkedin,
  instagram: FaInstagram,
  facebook: FaFacebook
};

const socialColors = {
  twitter: "hover:text-blue-400",
  linkedin: "hover:text-blue-600",
  instagram: "hover:text-pink-500",
  facebook: "hover:text-blue-500"
};

// Lottie Animation Components
const LoadingLottie = () => (
  <div className="w-16 h-16 mx-auto">
    <Lottie
      animationData={loadingAnimation}
      loop={true}
      style={{ width: 64, height: 64 }}
    />
  </div>
);

const SuccessLottie = () => (
  <div className="w-24 h-24 mx-auto mb-4">
    <Lottie
      animationData={successAnimation}
      loop={false}
      style={{ width: 96, height: 96 }}
    />
  </div>
);

const ErrorLottie = () => (
  <div className="w-20 h-20 mx-auto mb-4">
    <Lottie
      animationData={errorAnimation}
      loop={false}
      style={{ width: 80, height: 80 }}
    />
  </div>
);

const ContactHeaderLottie = () => (
  <div className="w-32 h-32 mx-auto mb-6">
    <Lottie
      animationData={contactAnimation}
      loop={true}
      style={{ width: 128, height: 128 }}
    />
  </div>
);

const SendButtonLottie = ({ isHovered }) => (
  <div className="w-8 h-8 mr-3">
    <Lottie
      animationData={sendAnimation}
      loop={isHovered}
      style={{ width: 32, height: 32 }}
    />
  </div>
);

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [settings, setSettings] = useState(null);
  const [contactInfo, setContactInfo] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [apiLoading, setApiLoading] = useState(true);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // Fetch settings from API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setApiLoading(true);
        const response = await axios.get(SETTINGS_URL);
        const apiData = response.data;

        // Find the active setting
        let setting = null;

        if (apiData.results && Array.isArray(apiData.results)) {
          setting = apiData.results[0] || apiData.results.find(s => s.isActive);
        } else if (Array.isArray(apiData)) {
          setting = apiData[0] || apiData.find(s => s.isActive);
        } else if (typeof apiData === 'object') {
          setting = apiData;
        }

        if (setting) {
          setSettings(setting);

          // Set up contact information dynamically
          const dynamicContactInfo = [];

          if (setting.contactUs?.email) {
            dynamicContactInfo.push({
              icon: FiMail,
              title: "Email Address",
              description: setting.contactUs.email,
              color: "text-purple-400",
            });
          }

          if (setting.contactUs?.phone) {
            dynamicContactInfo.push({
              icon: FiPhone,
              title: "Phone Number",
              description: setting.contactUs.phone,
              color: "text-green-400",
            });
          }

          if (setting.contactUs?.address) {
            dynamicContactInfo.push({
              icon: FiStar,
              title: "Office Address",
              description: setting.contactUs.address,
              color: "text-yellow-400",
            });
          }

          setContactInfo(dynamicContactInfo);

          // Set up social links dynamically
          const dynamicSocialLinks = [];

          if (setting.socialMedia) {
            Object.entries(setting.socialMedia).forEach(([platform, url]) => {
              if (url && socialIcons[platform]) {
                dynamicSocialLinks.push({
                  icon: socialIcons[platform],
                  href: url,
                  color: socialColors[platform] || "hover:text-blue-400",
                  platform: platform
                });
              }
            });
          }

          setSocialLinks(dynamicSocialLinks);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);

        // Fallback to default contact info
        setContactInfo([
          {
            icon: FiMail,
            title: "Email Address",
            description: "hello@company.com\nsupport@company.com",
            color: "text-purple-400",
          }
        ]);

        // Fallback to default social links
        setSocialLinks([
          { icon: FaTwitter, href: "#", color: "hover:text-blue-400", platform: "twitter" },
          { icon: FaLinkedin, href: "#", color: "hover:text-blue-600", platform: "linkedin" },
          { icon: FaInstagram, href: "#", color: "hover:text-pink-500", platform: "instagram" },
        ]);
      } finally {
        setApiLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const resetRecaptcha = () => {
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setSuccess({
        success: false,
        message: "❌ Please complete the reCAPTCHA verification"
      });
      return;
    }
    
    setLoading(true);
    setSuccess(null);

    try {
      const response = await axios.post(API_URL, {
        ...formData,
        recaptchaToken // Include the reCAPTCHA token in the request
      });

      setSuccess({
        success: true,
        message: "🎉 Message sent successfully! We'll get back to you soon."
      });

      // Clear form
      setFormData({ name: "", email: "", message: "", phone: "" });
      
      // Reset reCAPTCHA
      resetRecaptcha();

      // Redirect to home page after 5 seconds
      setTimeout(() => {
        navigate("/");
      }, 5000);

    } catch (error) {
      console.error("Error sending message:", error);
      setSuccess({
        success: false,
        message: `❌ ${error.response?.data?.message || error.message || "Message failed to send. Please try again."}`
      });
      
      // Reset reCAPTCHA on error
      resetRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      <FloatingParticles />

      {/* Animated background elements */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-10 left-10 w-20 h-20 bg-blue-500/10 rounded-full blur-xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-16 h-16 bg-purple-500/10 rounded-full blur-xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.2, 0.4],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-20"
        >
          <ContactHeaderLottie />

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Contact <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Us</span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            {settings?.contactDescription || "Ready to start your next project? Let's create something amazing together. We're here to help you bring your ideas to life."}
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
          {/* Contact Information */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="lg:col-span-1 space-y-6"
          >
            <motion.h2
              variants={itemVariants}
              className="text-3xl font-bold text-white mb-8 flex items-center"
            >
              <FiStar className="mr-3 text-yellow-400" />
              Let's Connect
            </motion.h2>

            {apiLoading ? (
              Array.from({ length: 2 }).map((_, index) => (
                <motion.div
                  key={`skeleton-${index}`}
                  variants={itemVariants}
                >
                  <AnimatedCard delay={index * 0.1}>
                    <div className="animate-pulse flex items-start space-x-4">
                      <div className="p-3 rounded-lg bg-gray-700/50">
                        <div className="w-6 h-6 bg-gray-600 rounded"></div>
                      </div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-600 rounded w-24 mb-2"></div>
                        <div className="h-3 bg-gray-600 rounded w-32"></div>
                      </div>
                    </div>
                  </AnimatedCard>
                </motion.div>
              ))
            ) : (
              contactInfo.map((item, index) => (
                <motion.div
                  key={item.title}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                >
                  <AnimatedCard delay={index * 0.1}>
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg bg-gray-700/50 ${item.color}`}>
                        <item.icon className="text-xl" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                        <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </AnimatedCard>
                </motion.div>
              ))
            )}

            {/* Social Links */}
            <motion.div
              variants={itemVariants}
              className="pt-6"
            >
              <AnimatedCard delay={0.4}>
                <h3 className="text-white font-semibold mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  {apiLoading ? (
                    Array.from({ length: 3 }).map((_, index) => (
                      <div
                        key={`social-skeleton-${index}`}
                        className="p-3 bg-gray-700/50 rounded-lg animate-pulse"
                      >
                        <div className="w-6 h-6 bg-gray-600 rounded"></div>
                      </div>
                    ))
                  ) : socialLinks.length > 0 ? (
                    socialLinks.map((social, index) => (
                      <motion.a
                        key={`${social.platform}-${index}`}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-3 bg-gray-700/50 rounded-lg text-gray-400 transition-all duration-300 border border-transparent hover:border-gray-600 ${social.color}`}
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <social.icon className="text-xl" />
                      </motion.a>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm">No social links available</p>
                  )}
                </div>
              </AnimatedCard>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <AnimatedCard delay={0.1}>
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
                <motion.h2
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-3xl font-bold text-white mb-2 flex items-center"
                >
                  <FaRocket className="mr-3 text-purple-400" />
                  Send Message
                </motion.h2>

                <p className="text-gray-400 mb-8">
                  {settings?.contactFormText || "Fill out the form below and we'll get back to you within 24 hours."}
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="relative"
                  >
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Your Name *
                    </label>
                    <div className="relative">
                      <FiUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        className="w-full pl-12 pr-4 py-4 bg-gray-700/50 rounded-xl border-2 border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 text-white placeholder-gray-400 backdrop-blur-sm"
                        required
                        minLength="2"
                        maxLength="100"
                        placeholder="Enter your full name"
                      />
                      <AnimatePresence>
                        {focusedField === 'name' && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          >
                            <FiCheck className="text-green-400 text-lg" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="relative"
                  >
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Your Email *
                    </label>
                    <div className="relative">
                      <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        className="w-full pl-12 pr-4 py-4 bg-gray-700/50 rounded-xl border-2 border-gray-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 text-white placeholder-gray-400 backdrop-blur-sm"
                        required
                        placeholder="Enter your email address"
                      />
                      <AnimatePresence>
                        {focusedField === 'email' && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          >
                            <FiCheck className="text-green-400 text-lg" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.55 }}
                  className="relative"
                >
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Your Phone (Optional)
                  </label>
                  <div className="relative">
                    <FiPhone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-700/50 rounded-xl border-2 border-gray-600 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-300 text-white placeholder-gray-400 backdrop-blur-sm"
                      placeholder="Enter your phone number"
                    />
                    <AnimatePresence>
                      {focusedField === 'phone' && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          <FiCheck className="text-green-400 text-lg" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="relative"
                >
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Your Message *
                  </label>
                  <div className="relative">
                    <FiMessageSquare className="absolute left-4 top-4 text-gray-500 text-lg" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-700/50 rounded-xl border-2 border-gray-600 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-300 text-white placeholder-gray-400 backdrop-blur-sm min-h-[150px] resize-none"
                      required
                      minLength="5"
                      maxLength="2000"
                      placeholder="Tell us about your project or inquiry..."
                    ></textarea>
                    <AnimatePresence>
                      {focusedField === 'message' && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          className="absolute right-3 top-4"
                        >
                          <FiCheck className="text-green-400 text-lg" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>

                {/* reCAPTCHA Component */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.65 }}
                  className="flex justify-center"
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <motion.button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-xl text-white font-bold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 group relative overflow-hidden"
                    whileHover={{
                      scale: 1.02,
                    }}
                    whileTap={{ scale: 0.98 }}
                    disabled={loading || !recaptchaToken}
                    onMouseEnter={() => setIsButtonHovered(true)}
                    onMouseLeave={() => setIsButtonHovered(false)}
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      {loading ? (
                        <>
                          <LoadingLottie />
                          <span className="ml-3">Sending Message...</span>
                        </>
                      ) : (
                        <>
                          <SendButtonLottie isHovered={isButtonHovered} />
                          Send Your Message
                          <motion.span
                            className="ml-2"
                            animate={{ y: [0, -5, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          >
                            🚀
                          </motion.span>
                        </>
                      )}
                    </span>

                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      initial={{ opacity: 0 }}
                    />

                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 0.8 }}
                    />
                  </motion.button>
                </motion.div>

                <AnimatePresence>
                  {success && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.9 }}
                      className={`text-center p-6 rounded-xl border-2 backdrop-blur-sm ${!success.success
                          ? "bg-red-500/10 text-red-400 border-red-500/30"
                          : "bg-green-500/10 text-green-400 border-green-500/30"
                        }`}
                    >
                      <div className="flex flex-col items-center justify-center space-y-2">
                        {success.success ? <SuccessLottie /> : <ErrorLottie />}
                        <span className="font-medium text-lg">
                          {success.message}
                          {success.success && (
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 1 }}
                              className="block text-sm mt-2 text-gray-300"
                            >
                              Redirecting to home page in 5 seconds...
                            </motion.span>
                          )}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </AnimatedCard>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}